import { useState } from 'react';
import { AlertCircle, ChevronDown } from 'lucide-react';

import { SoftCard } from '@/components/ui/soft-card';

/**
 * Sidebar card listing every product-data issue that still blocks at least one
 * of this product's channels, each with a Fix button. Sits directly below the
 * Visibility card, which shows the same blockage per channel rather than per
 * issue.
 *
 * Renders nothing when there's nothing to fix — an empty Issues card is noise,
 * and the Visibility card already says the product is visible everywhere.
 */
export function IssuesCard({ issues, onFix }) {
	const [open, setOpen] = useState(false);

	if (!issues.length) return null;

	return (
		<SoftCard className="!p-5" data-testid="issues-card">
			<button
				type="button"
				onClick={() => setOpen((o) => !o)}
				aria-expanded={open}
				className="flex w-full items-start justify-between gap-3 text-left"
			>
				<div>
					<div className="flex items-center gap-2">
						<h3 className="text-base font-bold">Issues</h3>
						<span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive/15 px-1.5 text-xs font-semibold text-destructive">
							{issues.length}
						</span>
					</div>
					<p className="mt-1 text-sm text-muted-foreground">Fix these to unlock more channels.</p>
				</div>
				<ChevronDown className={`mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
			</button>

			{open ? (
				<ul className="mt-4 space-y-1">
					{issues.map((issue) => (
						<li key={issue.type} data-testid={`issue-row-${issue.type}`} className="py-1.5">
							<div className="flex items-center justify-between gap-3">
								<div className="flex min-w-0 items-center gap-2.5">
									<span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-destructive/15 text-destructive">
										<AlertCircle className="h-3.5 w-3.5" />
									</span>
									<div className="min-w-0">
										<div className="text-sm font-semibold text-foreground">{issue.label}</div>
										<div className="text-xs text-muted-foreground">
											{`Affects ${issue.affectedChannels.length} channel${issue.affectedChannels.length === 1 ? '' : 's'}: ${issue.affectedChannels.join(', ')}`}
										</div>
									</div>
								</div>
								<button
									type="button"
									onClick={() => onFix(issue)}
									className="inline-flex h-7 shrink-0 items-center rounded-full bg-primary px-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
								>
									Fix
								</button>
							</div>
						</li>
					))}
				</ul>
			) : null}
		</SoftCard>
	);
}
